const PENDING_CLAIM_STORAGE_KEY = "wavemind.pendingRegistrationClaim";
const PENDING_CLAIM_SESSION_KEY = "wavemind.pendingClaim";
const PENDING_CLAIM_MAX_AGE = 24 * 60 * 60 * 1000;

declare global {
  interface Window {
    __wavemindPendingClaim?: { key: string; expiresAt: number };
  }
}

export const isLicenseKey = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const getLocalRedirect = (value: string | null | undefined): string | null => {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return null;
  }
  if (Array.from(value).some(character => character === "\\" || character.charCodeAt(0) < 32)) return null;

  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
};

export const getAuthRedirect = (search = window.location.search) =>
  getLocalRedirect(new URLSearchParams(search).get("redirect"));

export const withAuthRedirect = (path: string, redirect: string | null) => {
  const localRedirect = getLocalRedirect(redirect);
  return localRedirect ? `${path}${path.includes("?") ? "&" : "?"}redirect=${encodeURIComponent(localRedirect)}` : path;
};

const getClaimKey = (redirect: string | null) => {
  const localRedirect = getLocalRedirect(redirect);
  if (!localRedirect) return null;
  const url = new URL(localRedirect, window.location.origin);
  const key = url.searchParams.get("key");
  if (url.pathname !== "/claim") return null;
  return key && isLicenseKey(key) ? key : getPendingClaimKey() || null;
};

const readStoredClaim = (storageName: "localStorage" | "sessionStorage", storageKey: string): string => {
  try {
    const storage = window[storageName];
    const pending = JSON.parse(storage.getItem(storageKey) || "null");
    if (pending && typeof pending.key === "string" && isLicenseKey(pending.key) &&
      typeof pending.expiresAt === "number" && pending.expiresAt > Date.now()) {
      return pending.key;
    }
    storage.removeItem(storageKey);
  } catch {
    // A blocked or invalid storage entry must not interrupt claiming a license.
  }
  return "";
};

export const getPendingClaimKey = () => {
  const captured = window.__wavemindPendingClaim;
  return captured && isLicenseKey(captured.key) && captured.expiresAt > Date.now()
    ? captured.key : readStoredClaim("sessionStorage", PENDING_CLAIM_SESSION_KEY);
};

export const getClaimRedirect = (key: string): string => {
  if (!isLicenseKey(key)) return "/claim";
  const pending = { key, expiresAt: Date.now() + PENDING_CLAIM_MAX_AGE };
  window.__wavemindPendingClaim = pending;
  try {
    window.sessionStorage.setItem(PENDING_CLAIM_SESSION_KEY, JSON.stringify(pending));
    return "/claim";
  } catch {
    return `/claim?key=${encodeURIComponent(key)}`;
  }
};

// Email verification can open in another tab, so keep only this pending claim
// across tabs until it is claimed or the registration context expires.
export const rememberRegistrationClaim = (redirect: string | null) => {
  const key = getClaimKey(redirect);
  if (!key) return;
  try {
    window.localStorage.setItem(PENDING_CLAIM_STORAGE_KEY, JSON.stringify({
      key,
      expiresAt: Date.now() + PENDING_CLAIM_MAX_AGE,
    }));
  } catch {
    // The redirect query still preserves the claim when browser storage is disabled.
  }
};

export const getPendingRegistrationClaim = (): string | null => {
  const key = readStoredClaim("localStorage", PENDING_CLAIM_STORAGE_KEY);
  return key ? getClaimRedirect(key) : null;
};

export const clearPendingRegistrationClaim = (key: string) => {
  if (window.__wavemindPendingClaim?.key === key) delete window.__wavemindPendingClaim;
  try {
    if (getPendingClaimKey() === key) window.sessionStorage.removeItem(PENDING_CLAIM_SESSION_KEY);
    if (readStoredClaim("localStorage", PENDING_CLAIM_STORAGE_KEY) === key) {
      window.localStorage.removeItem(PENDING_CLAIM_STORAGE_KEY);
    }
  } catch {
    // Claiming the license has already succeeded even if storage is unavailable.
  }
};
